import React from "react";
import { LOGO_METALLIC } from "@/lib/api";
import Logo from "@/components/Logo";
import { SERVICES, servicePagePath, CONTACT } from "@/lib/content";
import { Phone, Mail, MapPin, MessageCircle, Instagram } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Footer() {
  const navigate = useNavigate();
  return (
    <footer
      data-testid="footer"
      className="relative border-t border-white/10 pt-20 pb-10 mt-8"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          <div className="md:col-span-5">
            <Logo height={112} />
            <p className="mt-8 max-w-md text-white/50 leading-relaxed text-sm">
              WTD Detailing &amp; Tint — Edmonton&apos;s premium studio for
              Paint Protection Film, Ceramic Coating and Auto Detailing.
            </p>
            <button
              onClick={() => navigate("/book")}
              data-testid="footer-book-btn"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1F5EFF] text-white text-sm font-semibold hover:bg-white hover:text-black transition-colors"
            >
              Book Now
            </button>
            <div className="mt-8 space-y-2.5 text-sm">
              <a href={`tel:${CONTACT.phone.replace(/[^\d+]/g, "")}`} data-testid="footer-phone" className="flex items-center gap-2.5 text-white/70 hover:text-white transition-colors">
                <Phone className="w-4 h-4 text-[#1F5EFF]" /> {CONTACT.phone}
              </a>
              <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" data-testid="footer-whatsapp" className="flex items-center gap-2.5 text-white/70 hover:text-white transition-colors">
                <MessageCircle className="w-4 h-4 text-[#1F5EFF]" /> WhatsApp Us
              </a>
              <a href="https://www.instagram.com/wtddetailing/" target="_blank" rel="noreferrer" data-testid="footer-instagram" className="flex items-center gap-2.5 text-white/70 hover:text-white transition-colors">
                <Instagram className="w-4 h-4 text-[#1F5EFF]" /> @wtddetailing
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="footer-email" className="flex items-center gap-2.5 text-white/70 hover:text-white transition-colors">
                <Mail className="w-4 h-4 text-[#1F5EFF]" /> {CONTACT.email}
              </a>
              <a href={`https://maps.google.com/?q=${encodeURIComponent(CONTACT.address)}`} target="_blank" rel="noreferrer" data-testid="footer-address" className="flex items-center gap-2.5 text-white/70 hover:text-white transition-colors">
                <MapPin className="w-4 h-4 text-[#1F5EFF]" /> {CONTACT.address}
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="font-mono-alt text-[10px] text-white/40 mb-5">
              Services
            </div>
            <ul className="space-y-3">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <a
                    href={servicePagePath(s.slug)}
                    className="text-white/70 hover:text-white text-sm transition-colors"
                  >
                    {s.title}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="/services"
                  className="text-[#1F5EFF] hover:text-white text-sm transition-colors"
                >
                  All Services →
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="font-mono-alt text-[10px] text-white/40 mb-5">
              Navigate
            </div>
            <ul className="grid grid-cols-2 gap-y-3 gap-x-6">
              {[
                { l: "Services", h: "/services" },
                { l: "Blog", h: "/blog" },
                { l: "FAQ", h: "/faq" },
                { l: "Contact", h: "/contact" },
              ].map((n) => (
                <li key={n.l}>
                  <a
                    href={n.h}
                    className="text-white/70 hover:text-white text-sm transition-colors"
                  >
                    {n.l}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16 divider-line" />

        <div className="mt-8 flex flex-col md:flex-row md:items-center justify-between gap-4 text-xs text-white/40">
          <div>© {new Date().getFullYear()} WTD Detailing & Tint. All rights reserved.</div>
          <div className="flex items-center gap-5 flex-wrap">
            <a href="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</a>
            <a href="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">Terms of Service</a>
            <a href="/cookies" className="hover:text-white transition-colors" data-testid="footer-cookies">Cookies</a>
          </div>
          <div className="font-mono-alt text-[10px]">
            Edmonton · Alberta · Canada
          </div>
        </div>
      </div>
    </footer>
  );
}
